import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import Tooltip from "@mui/material/Tooltip";
import TextInput from "../../../../components/ui/TextInput";
import {
    getScannableObjectTags,
    createScannableObjectTag,
    updateScannableObjectTag,
    deleteScannableObjectTag,
    ScannableObjectTagResponse,
} from "../../../../api/progression";
import i18n from "../../../../i18n";
import styles from "./ScannableObjectTagsModal.module.css";

interface ScannableObjectTagsModalProps {
    isOpen: boolean;
    onClose: () => void;
    scanConfigId: string;
    language: string;
}

const ScannableObjectTagsModal: React.FC<ScannableObjectTagsModalProps> = ({
    isOpen,
    onClose,
    scanConfigId,
    language,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const modalContentRef = useRef<HTMLDivElement>(null);
    const token = localStorage.getItem("token") || "";

    const [tags, setTags] = useState<ScannableObjectTagResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [newTagName, setNewTagName] = useState("");
    const [editingTagId, setEditingTagId] = useState<string | null>(null);
    const [editName, setEditName] = useState("");
    const [error, setError] = useState<string | null>(null);

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
            fetchTags();
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isOpen, handleOutsideClick]);

    const fetchTags = async () => {
        if (!scanConfigId) return;

        setLoading(true);
        setError(null);
        try {
            const response = await getScannableObjectTags({ token, scanConfigId });
            if (response?.scannableObjectTags) {
                setTags(response.scannableObjectTags);
            }
        } catch (error) {
            console.error("Error fetching tags:", error);
            setError(t("errorFetchingTags"));
        } finally {
            setLoading(false);
        }
    };

    const handleCreateTag = async () => {
        if (!newTagName.trim() || !scanConfigId) return;

        setLoading(true);
        setError(null);
        try {
            await createScannableObjectTag({
                token,
                scanConfigId,
                name: newTagName.trim(),
                language: language || "EN",
            });
            setNewTagName("");
            await fetchTags();
        } catch (error) {
            console.error("Error creating tag:", error);
            setError(t("errorCreatingTag"));
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateTag = async (tagId: string) => {
        if (!editName.trim()) return;

        setLoading(true);
        setError(null);
        try {
            await updateScannableObjectTag({
                token,
                scannableObjectTagId: tagId,
                name: editName.trim(),
                language: language || "EN",
            });
            setEditingTagId(null);
            setEditName("");
            await fetchTags();
        } catch (error) {
            console.error("Error updating tag:", error);
            setError(t("errorUpdatingTag"));
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTag = async (tagId: string, tagName: string) => {
        if (!window.confirm(t("confirmDeleteTag", { name: tagName }))) return;

        setLoading(true);
        setError(null);
        try {
            await deleteScannableObjectTag({ token, scannableObjectTagId: tagId });
            await fetchTags();
        } catch (error) {
            console.error("Error deleting tag:", error);
            setError(t("errorDeletingTag"));
        } finally {
            setLoading(false);
        }
    };

    const startEditing = (tag: ScannableObjectTagResponse) => {
        setEditingTagId(tag.id);
        setEditName(getTagLocalizedName(tag));
    };

    const cancelEditing = () => {
        setEditingTagId(null);
        setEditName("");
    };

    const getTagLocalizedName = (tag: ScannableObjectTagResponse) => {
        const langCode = i18n.language?.toUpperCase();
        const localized = tag.scannableObjectTagLocalized?.find((loc) => loc.language?.toUpperCase() === langCode);
        return localized?.name || tag.scannableObjectTagLocalized?.[0]?.name || t("unnamed");
    };

    if (!isOpen) return null;

    const modal = (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent} ref={modalContentRef}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>{t("manageObjectTags")}</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    {error && <div className={styles.errorMessage}>{error}</div>}

                    {/* Create new tag */}
                    <div className={styles.createSection}>
                        <h3 className={styles.sectionTitle}>
                            {t("createNewTag")}
                            <Tooltip title={t("doNotTranslateTooltip")} arrow>
                                <span className="tooltip-icon" style={{ marginLeft: 8, marginBottom: 0 }}>
                                    i
                                </span>
                            </Tooltip>
                        </h3>
                        <div className={styles.createForm}>
                            <div className={styles.createInput}>
                                <TextInput
                                    placeholder={t("enterTagName")}
                                    value={newTagName}
                                    onChange={(e) => setNewTagName(e.target.value)}
                                    size="small"
                                    onKeyDown={(e: React.KeyboardEvent) => {
                                        if (e.key === "Enter" && newTagName.trim()) {
                                            handleCreateTag();
                                        }
                                    }}
                                />
                            </div>
                            <button
                                type="button"
                                className={styles.createButton}
                                onClick={handleCreateTag}
                                disabled={loading || !newTagName.trim()}
                            >
                                {t("create")}
                            </button>
                        </div>
                    </div>

                    {/* Tags list */}
                    <div className={styles.tagsSection}>
                        <h3 className={styles.sectionTitle}>{t("existingTags")}</h3>
                        {loading && tags.length === 0 ? (
                            <div className={styles.loading}>{t("loading")}</div>
                        ) : tags.length === 0 ? (
                            <div className={styles.emptyState}>{t("noTagsYet")}</div>
                        ) : (
                            <div className={styles.tagsList}>
                                {tags.map((tag) => (
                                    <div key={tag.id} className={styles.tagItem}>
                                        {editingTagId === tag.id ? (
                                            <div className={styles.editForm}>
                                                <TextInput
                                                    value={editName}
                                                    onChange={(e) => setEditName(e.target.value)}
                                                    placeholder={t("enterTagName")}
                                                    size="small"
                                                    onKeyDown={(e: React.KeyboardEvent) => {
                                                        if (e.key === "Enter" && editName.trim()) {
                                                            handleUpdateTag(tag.id);
                                                        }
                                                        if (e.key === "Escape") {
                                                            cancelEditing();
                                                        }
                                                    }}
                                                />
                                                <div className={styles.editActions}>
                                                    <button
                                                        className={styles.cancelButton}
                                                        onClick={cancelEditing}
                                                        disabled={loading}
                                                    >
                                                        {t("cancel")}
                                                    </button>
                                                    <button
                                                        className={styles.saveButton}
                                                        onClick={() => handleUpdateTag(tag.id)}
                                                        disabled={loading || !editName.trim()}
                                                    >
                                                        {t("save")}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className={styles.tagInfo}>
                                                    <span className={styles.tagName}>{getTagLocalizedName(tag)}</span>
                                                    <span className={styles.tagCount}>
                                                        ({tag.objectCount || 0} {t("objects")})
                                                    </span>
                                                </div>
                                                <div className={styles.tagActions}>
                                                    <button
                                                        className={styles.actionButton}
                                                        onClick={() => startEditing(tag)}
                                                        disabled={loading}
                                                    >
                                                        {t("edit")}
                                                    </button>
                                                    <button
                                                        className={`${styles.actionButton} ${styles.deleteButton}`}
                                                        onClick={() =>
                                                            handleDeleteTag(tag.id, getTagLocalizedName(tag))
                                                        }
                                                        disabled={loading || (tag.objectCount || 0) > 0}
                                                        title={
                                                            (tag.objectCount || 0) > 0
                                                                ? t("cannotDeleteTagWithObjects")
                                                                : t("delete")
                                                        }
                                                    >
                                                        {t("delete")}
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default ScannableObjectTagsModal;
